import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DataTable,
  DataTableHead,
  DataTableRow,
  Fieldset,
  Input,
  Heading,
  Checkbox,
  Select,
  SelectOption,
  Link as HCRLink,
  Tag,
  Spinner,
  Popup,
  PopupSection,
  Button,
  Textarea,
} from '@flixbus/honeycomb-react';
import { DateTime } from 'luxon';
import {
  Icon,
  IconMagnifier,
  IconAccount,
  IconCalendar,
  IconDot,
  IconCheckmarkStrong,
  IconEdit,
  IconReset,
} from '@flixbus/honeycomb-icons-react';
import { DiscountTypeMap, Voucher } from '../../types/Voucher';
import { Pagination } from '../../components/Pagination';
import { useRoles } from '../../context/RolesProvider';
import { OriginalAmount } from '../../components/OriginalAmount';
import { CreditBalance } from '../../components/CreditBalance';
import { VoucherActions } from '../../components/VoucherActions';
import { DiscountBalance } from '../../components/DiscountBalance';
import { useBatchUpdate } from '../../context/VoucherBatchUpdateProvider';
import { VoucherFilter } from '../../types/VoucherFilter';
import { useVoucherList } from '../../context/VoucherListProvider';
import { useNotification } from '../../components/Notifications/Context';
import { useMsal } from '@azure/msal-react';

import styles from './dashboard.module.scss';
import { ROLES } from '../../types/Roles';
import { formatChannels } from '../../helpers/allowedChannels';
import isVoucherOwner from '../../helpers/isVoucherOwner';
import { CopyVoucherCode } from '../../components/CopyVoucherCode';

const statuses = [
  { value: '', label: 'Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'cancel', label: 'Disabled' },
  { value: 'used', label: 'Used' },
  { value: 'expired', label: 'Expired' },
];

type ListHeadingProps = {
  filters: VoucherFilter;
  setFilters: React.Dispatch<React.SetStateAction<VoucherFilter>>;
  loading: boolean;
};

const ListHeading: React.FC<ListHeadingProps> = ({ filters, setFilters, loading }) => {
  const handleFilterChange = (field: string, value: string | number) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 1,
      [field]: value,
    }));
  };

  const filtersApplied = filters.code !== '' || filters.createdBy !== '' || filters.status !== '';

  return (
    <div>
      <div className={styles.listheading}>
        <Heading size={2}>Voucher list</Heading>
        <Fieldset horizontal itemSpacing='2'>
          <Input
            id='code-search'
            aria-label='Search'
            type='search'
            placeholder='Search by Code/Prefix'
            iconLeft={<Icon appearance='primary' InlineIcon={IconMagnifier} />}
            value={filters.code}
            autoComplete='off'
            loading={loading}
            onChange={e => handleFilterChange('code', e.target.value.trim())}
            disabled={loading}
            {...(filters.code ? { valid: true } : {})}
          />
          <Input
            id='createdby-search'
            aria-label='Created by'
            type='search'
            placeholder='Created by'
            iconLeft={<Icon appearance='primary' InlineIcon={IconAccount} />}
            value={filters.createdBy}
            autoComplete='off'
            loading={loading}
            onChange={e => handleFilterChange('createdBy', e.target.value.trim())}
            disabled={loading}
            {...(filters.createdBy ? { valid: true } : {})}
          />
          <Select
            extraClasses={styles.select}
            id='voucher-status'
            aria-label='Filter by status'
            placeholder='Status'
            value={filters.status}
            onChange={e => handleFilterChange('status', e.target.value)}
            disabled={loading}
            {...(filters.status ? { valid: true } : {})}
          >
            {statuses.map(({ value, label }) => (
              <SelectOption key={value} value={value}>
                {label}
              </SelectOption>
            ))}
          </Select>
          <Button
            display='square'
            aria-label='Reset filters'
            disabled={!filtersApplied || loading}
            onClick={() =>
              setFilters({
                code: '',
                createdBy: '',
                status: '',
                page: 1,
                limit: filters.limit,
              })
            }
            loading={loading}
          >
            <Icon InlineIcon={IconReset} aria-hidden='true' />
          </Button>
        </Fieldset>
      </div>
    </div>
  );
};

const VoucherList = () => {
  const {
    vouchers,
    filters,
    setFilters,
    totalPages,
    totalCount,
    loading,
    addOrUpdateComment,
    refetch,
  } = useVoucherList();

  const batchUpdateContext = useBatchUpdate();
  const selectedVouchers = batchUpdateContext.payload.batches[0].ids;
  const { toggleId } = batchUpdateContext;

  const includesAll = vouchers.every(v => selectedVouchers.includes(v.id));
  const { hasAnyRole, isAdmin, hasRole } = useRoles();

  const [activeCommentPopupId, setActiveCommentPopupId] = useState<string | null>(null);
  const popupTargetRef = useRef<HTMLButtonElement | null>(null);

  const [commentValue, setCommentValue] = useState('');
  const [loadingComment, setLoadingComment] = useState(false);

  const { pushToast } = useNotification();

  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username;

  const handleFilterChange = (field: string, value: string | number) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 1,
      [field]: value,
    }));
  };

  const handlePageChange = (page: number) => {
    handleFilterChange('page', page);
  };

  const requiredRolesBasedOnPrefix = (prefix: string): string[] => {
    switch (prefix) {
      case 'CUS':
      case 'CUSKU':
        return [ROLES.ROLE_MANAGE_GOODWILL_VOUCHER, ROLES.ROLE_UPDATE_MULTIPLE_VOUCHERS];
      case 'BGL':
        return [ROLES.ROLE_MANAGE_ACCOMPANYING_PERSON_VOUCHER, ROLES.ROLE_UPDATE_MULTIPLE_VOUCHERS];
      case 'AGTPEP':
        return [ROLES.ROLE_MANAGE_AGTPEP_VOUCHER, ROLES.ROLE_UPDATE_MULTIPLE_VOUCHERS];
      default:
        return [ROLES.ROLE_UPDATE_MULTIPLE_VOUCHERS];
    }
  };

  const hasAccessBasedOnPrefix = (prefix: string) => {
    return isAdmin || hasAnyRole(requiredRolesBasedOnPrefix(prefix));
  };

  const handleSelectAllCheckbox = (vouchers: Voucher[]) => {
    vouchers.forEach(voucher => {
      if (hasAccessBasedOnPrefix(voucher.prefix)) {
        if (includesAll) {
          toggleId(voucher);
        } else {
          if (!selectedVouchers.includes(voucher.id)) {
            toggleId(voucher);
          }
        }
      }
    });
  };

  const showSearchResultCount = !!filters.code || !!filters.createdBy || !!filters.status;

  return (
    <>
      <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '6px' }}>
        <ListHeading filters={filters} setFilters={setFilters} loading={loading} />
        <VoucherActions />
        {showSearchResultCount && (
          <Heading size={3}>
            {totalCount > 500 ? 'Over 500' : `${totalCount}`} results for your search
          </Heading>
        )}
        {loading ? (
          <Spinner extraClasses={styles.spinner} size={'xl'} />
        ) : isAdmin || hasRole(ROLES.ROLE_VIEW_VOUCHER_LIST) ? (
          <DataTable
            extraClasses={styles.table}
            plain={true}
            small={false}
            collapsed={false}
            scrollable={true}
            headers={
              <DataTableHead extraClasses={styles.head}>
                <th className={styles.header} scope='col'>
                  <Checkbox
                    small
                    aria-label='checked'
                    id='checked'
                    value='checked'
                    checked={includesAll}
                    onChange={() => handleSelectAllCheckbox(vouchers)}
                  />
                </th>
                <th className={styles.header} scope='col'>
                  Code
                </th>
                <th className={styles.header} scope='col'>
                  Created by
                </th>
                <th className={styles.header} scope='col'>
                  Status
                </th>
                <th className={styles.header} scope='col'>
                  Usage type
                </th>
                <th className={styles.header} scope='col'>
                  Discount type
                </th>
                <th className={styles.header} scope='col'>
                  Original amount
                </th>
                <th className={styles.header} scope='col'>
                  Credit balance
                </th>
                <th className={styles.header} scope='col'>
                  Discount balance
                </th>
                <th className={styles.header} scope='col'>
                  Allowed platforms
                </th>
                <th className={styles.header} scope='col'>
                  Creation
                </th>
                <th className={styles.header} scope='col'>
                  Valid from
                </th>
                <th className={styles.header} scope='col'>
                  Valid until
                </th>
                <th className={styles.header} scope='col'>
                  Specification
                </th>
                <th className={styles.header} scope='col'>
                  Comments
                </th>
              </DataTableHead>
            }
          >
            {vouchers.map(voucher => {
              const createdAtFormatted =
                DateTime.fromISO(voucher.createdAt).setLocale('de').toFormat('dd LLL yyyy') ||
                'N/A';

              const validFromFormatted =
                DateTime.fromISO(voucher.validityInterval.validSince)
                  .setLocale('de')
                  .toFormat('dd LLL yyyy') || 'N/A';

              const validUntilFormatted =
                DateTime.fromISO(voucher.validityInterval.validUntil)
                  .setLocale('de')
                  .toFormat('dd LLL yyyy') || 'N/A';

              const disabledStatuses = ['inactive', 'cancel', 'expired'];
              const isOwnVoucher = isVoucherOwner(voucher.createdBy, userEmail);

              return (
                <DataTableRow key={voucher.code}>
                  <td className={styles.header}>
                    <Checkbox
                      small
                      aria-label={`Choose ${voucher.code}`}
                      checked={selectedVouchers.includes(voucher.id)}
                      onChange={() => toggleId(voucher)}
                      id={`voucher-checkbox-${voucher.id}`}
                      value={voucher.code}
                      label=''
                      disabled={!hasAccessBasedOnPrefix(voucher.prefix)}
                    />
                  </td>
                  <td className={styles.cell}>
                    <div className={styles.codeCell}>
                      <HCRLink
                        extraClasses={styles.link}
                        RouterLink={Link}
                        to={`/voucher/${voucher.id}`}
                      >
                        {voucher.code}
                      </HCRLink>
                      {isOwnVoucher && <CopyVoucherCode code={voucher.code} showLabel={false} />}
                    </div>
                  </td>
                  <td className={styles.cell}>
                    {voucher.createdBy ? (
                      <Tag
                        {...(!isOwnVoucher ? { display: 'outlined' } : {})}
                        tagColor={
                          isOwnVoucher
                            ? 'var(--hcr-grayscale-100-color)'
                            : 'var(--hcr-grayscale-50-color)'
                        }
                        tagTextColor={
                          isOwnVoucher
                            ? 'var(--hcr-grayscale-0-color'
                            : 'var(--hcr-grayscale-100-color)'
                        }
                      >
                        {voucher.createdBy}
                      </Tag>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td className={styles.cell}>
                    <Tag
                      {...(voucher.status === 'used'
                        ? {
                            tagColor: 'var(--hcr-success-dark-color)',
                            tagTextColor: 'var(--hcr-success-dark-color)',
                          }
                        : voucher.status === 'active'
                          ? {
                              tagColor: 'var(--hcr-neutral-color)',
                              tagTextColor: 'var(--hcr-neutral-color)',
                            }
                          : {})}
                      disabled={disabledStatuses.includes(voucher.status)}
                      display='outlined'
                    >
                      {voucher.status === 'active' && <Icon InlineIcon={IconDot} />}
                      {voucher.status === 'used' && <Icon InlineIcon={IconCheckmarkStrong} />}
                      {Object.values(statuses).find(s => s.value === voucher.status)?.label}
                    </Tag>
                  </td>
                  <td className={styles.cell}>{voucher.multiUsage ? 'Multiple' : 'Single'}</td>
                  <td className={styles.cell}>{DiscountTypeMap[voucher.discountType.type]}</td>
                  <td>
                    <OriginalAmount voucher={voucher} />
                  </td>
                  <td>
                    <CreditBalance discountType={voucher.discountType} />
                  </td>
                  <td>
                    <DiscountBalance discountType={voucher.discountType} />
                  </td>
                  <td className={styles.cell}>{formatChannels(voucher.rules)}</td>
                  <td className={styles.cell}>
                    <Tag extraClasses={styles.tag} display='outlined'>
                      <Icon appearance='primary' InlineIcon={IconCalendar} />
                      {createdAtFormatted}
                    </Tag>
                  </td>
                  <td className={styles.cell}>
                    <Tag extraClasses={styles.tag} display='outlined'>
                      <Icon appearance='primary' InlineIcon={IconCalendar} />
                      {validFromFormatted}
                    </Tag>
                  </td>
                  <td className={styles.cell}>
                    <Tag
                      extraClasses={styles.tag}
                      display='outlined'
                      {...(DateTime.fromISO(voucher.validityInterval.validSince) < DateTime.now()
                        ? { appearance: 'danger' }
                        : {})}
                    >
                      <Icon
                        style={{ fill: 'var(--hcr-danger-color)' }}
                        appearance='primary'
                        InlineIcon={IconCalendar}
                      />
                      {validUntilFormatted}
                    </Tag>
                  </td>
                  <td className={styles.cell}>{voucher.specification || 'N/A'}</td>
                  <td className={styles.cell}>
                    <Button
                      id={`comment-btn-${voucher.id}`}
                      appearance='link'
                      onClick={() => setActiveCommentPopupId(voucher.id)}
                      extraClasses={styles.comment}
                      aria-label={
                        voucher.comments.length > 0
                          ? `View comments for voucher ${voucher.code}`
                          : `Add comment to voucher ${voucher.code}`
                      }
                    >
                      <Icon
                        InlineIcon={IconEdit}
                        appearance='primary'
                        extraClasses={styles.commentIcon}
                      />
                      {voucher.comments.length > 0 &&
                        voucher.comments.map(c => c.content).join(', ')}
                    </Button>
                  </td>
                </DataTableRow>
              );
            })}
          </DataTable>
        ) : (
          <span>You have no permission to view the voucher list</span>
        )}
      </div>
      <Popup
        active={!!activeCommentPopupId}
        onOverlayClick={() => {
          setActiveCommentPopupId(null);
          setCommentValue('');
        }}
        targetRef={popupTargetRef}
      >
        <PopupSection type='icon'>
          <Icon InlineIcon={IconEdit} size={6} />
        </PopupSection>
        <PopupSection type='title'>
          <Heading size={3} sectionHeader flushSpace>
            Add Comment
          </Heading>
        </PopupSection>
        <PopupSection type='content' extraClasses={styles.popupSection}>
          {vouchers
            .find(v => v.id === activeCommentPopupId)
            ?.comments.map(comment => (
              <div key={comment.id} className={styles.commentContainer}>
                <div>
                  {comment.createdAt
                    ? `${DateTime.fromISO(comment.createdAt).toFormat('dd.MM.yyyy')} | ${comment.author}`
                    : `unknown date | ${comment.author}`}
                </div>
                <div>{comment.content}</div>
              </div>
            ))}
          <Textarea
            name='comment'
            id='comment'
            aria-label='Comment'
            value={commentValue}
            maxLength={255}
            onChange={e => setCommentValue(e.target.value)}
          />
        </PopupSection>
        <PopupSection type='actions'>
          <Button
            size='md'
            onClick={() => {
              setActiveCommentPopupId(null);
              setCommentValue('');
            }}
          >
            Cancel
          </Button>
          <Button
            appearance='primary'
            size='md'
            loading={loadingComment}
            onClick={async () => {
              if (!activeCommentPopupId || commentValue.length < 10) {
                pushToast({
                  message: 'Comment must be at least 10 characters long',
                  status: 'warning',
                });
                return;
              }
              setLoadingComment(true);
              await addOrUpdateComment(activeCommentPopupId, commentValue.trim());
              setCommentValue('');
              setActiveCommentPopupId(null);
              refetch();
              setLoadingComment(false);
            }}
          >
            Submit
          </Button>
        </PopupSection>
      </Popup>
      <div className={styles.pagination}>
        <Pagination
          totalPages={totalPages}
          currentPage={filters.page}
          onPageChange={handlePageChange}
          loading={loading}
        />
      </div>
    </>
  );
};

export default VoucherList;
